import { read_timeline_value_label } from "../../../utils/timeline/timeline_formatting";
import {
    read_sales_session_change,
    read_sales_session_event_type,
    read_sales_session_events,
    read_sales_session_evidence
} from "../salesSession/sales_session_view";
import { read_sales_session_seller } from "../salesSession/sales_session_view";

const sellerChangeTypes = ["SELLER", "SELLER_ASSIGNED", "SELLER_REMOVED"];

const title_case = (value) => {
    return `${value || ""}`.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const read_person_name = (value, fallback) => {
    const person = value && typeof value === "object" ? value : {};
    const structuredName = [person.firstName || person.firstname, person.lastName || person.lastname].filter(Boolean).join(" ");
    const scalarValue = value && typeof value !== "object" ? value : "";

    return `${person.name || person.label || person.displayName || structuredName || person.email || person.username || person.id || scalarValue || fallback}`.trim();
};

const placeholderSellerNames = new Set(["system", "unknown seller", "unknown contract seller"]);

// Rejects technical actor fallbacks before selecting the seller displayed in the card header.
const read_valid_meeting_seller = (value) => {
    const person = value && typeof value === "object" ? value : { name: value };
    const identities = [
        person.userId,
        person.id,
        person._id,
        person.email,
        person.username,
        person.name,
        person.label,
        person.displayName
    ].map((identity) => `${identity || ""}`.trim()).filter(Boolean);

    return identities.length && identities.some((identity) => !placeholderSellerNames.has(identity.toLowerCase()))
        ? value
        : null;
};

// Resolves the immutable contract-seller snapshot retained on the completed tenure.
export const read_meeting_session_seller = (event, teamMembers = []) => {
    const outcome = read_meeting_session_outcome(event);
    const savedSellerCandidates = [
        event?.data?.seller,
        event?.detail?.seller,
        event?.data?.caller,
        event?.detail?.caller,
        event?.from?.seller
    ];
    const terminalActorCandidates = ["Paid", "Lost"].includes(outcome)
        ? [event?.by, event?.actor]
        : [];
    const savedSeller = [...savedSellerCandidates, ...terminalActorCandidates]
        .map(read_valid_meeting_seller)
        .find(Boolean);

    if (!savedSeller) {
        return { name: "Unknown contract seller" };
    }

    const seller = read_sales_session_seller({
        ...event,
        data: {
            ...(event?.data || {}),
            seller: savedSeller
        }
    }, teamMembers);

    return seller.name === "Unknown seller" ? { name: "Unknown contract seller" } : seller;
};

// Maps terminal board outcomes to the labels used by Meeting cards.
export const read_meeting_session_outcome = (event) => {
    const outcome = `${event?.data?.boundaryReason || event?.data?.outcome || event?.to?.outcome || event?.to?.status || ""}`.trim().toLowerCase();
    const outcomeLabels = {
        paid: "Paid",
        lost: "Lost",
        seller_changed: "Seller changed"
    };

    return outcomeLabels[outcome] || title_case(outcome || "Update");
};

// Reads the old/new seller pair and authenticated operator from one boundary event.
const read_meeting_seller_change = (event) => {
    const sellerChange = (event?.changes || event?.data?.changes || []).map(read_sales_session_change).find((change) => {
        return ["seller", "caller", "contractseller"].includes(`${change?.field || ""}`.trim().toLowerCase());
    });
    const previousSeller = sellerChange?.from || event?.from?.seller || event?.from || {};
    const nextSeller = sellerChange?.to || event?.to?.seller || event?.to || {};
    const actor = event?.actor || event?.by || {};

    return {
        key: event.salesEventKey,
        at: new Date(event?.at || event?.changedAt || 0).getTime() || 0,
        scenario: "seller_change",
        previousSeller: read_person_name(previousSeller, "Unknown contract seller"),
        nextSeller: read_person_name(nextSeller, "Unknown contract seller"),
        actor: read_person_name(actor, "Unknown actor")
    };
};

// Combines ordinary meeting activity with the seller swap that closes the old tenure.
export const read_meeting_session_events = (event) => {
    const regularEvents = read_sales_session_events(event);
    const sellerEvents = read_sales_session_evidence(event).filter((sessionEvent) => {
        return sellerChangeTypes.includes(read_sales_session_event_type(sessionEvent));
    }).map(read_meeting_seller_change);

    return [...regularEvents, ...sellerEvents].sort((firstEvent, secondEvent) => {
        return firstEvent.at - secondEvent.at || firstEvent.key.localeCompare(secondEvent.key);
    }).map((sessionEvent) => ({
        ...sessionEvent,
        previousSeller: read_timeline_value_label(sessionEvent.previousSeller, "Unknown contract seller"),
        nextSeller: read_timeline_value_label(sessionEvent.nextSeller, "Unknown contract seller")
    }));
};
